/* eslint-disable no-unreachable */
import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { get, pick, map, keys, find } from "lodash";
import { Formik } from "formik";
import {
  TextField,
  IconButton,
  InputAdornment,
  InputBase,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Dropzone from "react-dropzone";
import {
  createCertificate,
  updateCertificate,
  getCertificate,
} from "../../apis/admin";
import { CreateCertificateSchema } from "../../globals/validations";
import {
  POONAWALLA_DIALOG_TYPES,
  POONAWALLA_USER_ROLES_TYPES,
  PAGE_MODES,
  ROUTE_DASHBOARD,
  ROUTE_SIGNIN,
} from "../../globals/constants";
import { showErrorToaster, showSuccessToaster } from "../../globals/toaster";
import { RefreshDataActions } from "../../redux/reducers/RefreshDataReducer";
import { verifyToken } from "../../globals/utils/jwtUtils";

import PoonaWallaModal from "../PoonaWallaModal";
import PoonaWallaBasicLoader from "../PoonaWallaBasicLoader";

const CertificateModal = () => {
  const { authData } = useSelector(
    (store) => ({
      authData: store.auth.data || {},
    }),
    shallowEqual
  );

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const qp = new URLSearchParams(location.search);

  const initialFormValues = {
    name: "",
    description: "",
    certificate: "",
    key: "",
    pfx: "",
    passphrase: "",
    showPassword: true,
  };

  const [pageMode, setPageMode] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [certificateData, setCertificateData] = useState(initialFormValues);

  const handleUnmount = () => {
    // Remove Qparam & replace
    qp.delete("certificatemanagermodal");
    history.replace({
      search: qp.toString(),
    });
  };

  const handleCertificateConfig = async (
    values,
    { setSubmitting, setFieldError, setStatus }
  ) => {
    try {
      setSubmitting(true);
      const { showPassword, ...newValues } = values;
      if (pageMode === PAGE_MODES.CREATE) {
        await createCertificate({
          ...newValues,
          name: get(values, "name"),
          description: get(values, "description"),
          certificate: btoa(get(values, "certificate", "")),
          key: btoa(get(values, "key", "")),
          pfx: btoa(get(values, "pfx", "")),
        });
        showSuccessToaster("Certificate Created.");
      } else {
        await updateCertificate(qp.get("certificatemanagermodal"), {
          ...newValues,
          name: get(values, "name"),
          description: get(values, "description"),
          certificate: btoa(get(values, "certificate", "")),
          key: btoa(get(values, "key", "")),
          pfx: btoa(get(values, "pfx", "")),
        });
        showSuccessToaster("Certificate Updated.");
      }

      handleUnmount();
      dispatch(RefreshDataActions.SET_TO_REFRESH());
    } catch (error) {
      const data = get(error, "data");
      if (get(data, "type") === POONAWALLA_DIALOG_TYPES.FIELD) {
        map(keys(get(data, "detail")), (key) =>
          setFieldError(key, get(data, ["detail", key], ""))
        );
      } else {
        showErrorToaster(get(error, "data.detail", "Something went wrong."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGetCertificate = async (id) => {
    try {
      setIsLoading(true);
      const { data } = await getCertificate(id);
      const decodedData = {
        name: data.name,
        description: data.description,
        certificate: atob(data.certificate),
        key: atob(data.key),
        pfx: atob(data.pfx),
        passphrase: data.passphrase,
      };
      setCertificateData(decodedData);
    } catch (error) {
      showErrorToaster(get(error, "data.detail", "Something went wrong."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (qp.get("certificatemanagermodal")) {
      // IF NOT LOGGED IN
      if (!get(authData, "idToken") || !verifyToken(get(authData, "idToken"))) {
        history.push(ROUTE_SIGNIN);
      } else if (
        // IF LOGGED IN BUT NOT AN ADMIN, THEN THROW HIM INTO DASHBOARD
        ![
          POONAWALLA_USER_ROLES_TYPES.types.ADMIN,
          POONAWALLA_USER_ROLES_TYPES.types.MAKER,
          POONAWALLA_USER_ROLES_TYPES.types.CHECKER,
        ].includes(get(authData, "user_type"))
      ) {
        history.push(ROUTE_DASHBOARD);
      } else {
        // CREATE APPLICATION MODE
        if (qp.get("certificatemanagermodal") === "new") {
          setPageMode(PAGE_MODES.CREATE);
        } else {
          // UPDATE APPLICATION MODE
          setPageMode(PAGE_MODES.EDIT);
          handleGetCertificate(qp.get("certificatemanagermodal"));
        }
      }
    } else {
      setPageMode(undefined);
      setCertificateData(initialFormValues);
    }
  }, [location.search]);

  return (
    <>
      {pageMode && (
        <PoonaWallaModal
          size="lg"
          handleHideModal={handleUnmount}
          isActive={Boolean(pageMode)}
          title={
            pageMode === PAGE_MODES.CREATE
              ? "Create Certificate"
              : "Update Certificate"
          }
        >
          {isLoading ? (
            <PoonaWallaBasicLoader isLoading={isLoading} />
          ) : (
            <div className="CreateCertificateModal">
              <Formik
                initialValues={certificateData}
                validationSchema={CreateCertificateSchema()}
                onSubmit={handleCertificateConfig}
              >
                {({
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  handleBlur,
                  handleSubmit,
                  isSubmitting,
                  handleChange,
                }) => (
                  <>
                    <form onSubmit={handleSubmit}>
                      <div className="mt-4">
                        <span className="font-weight-bold text-secondary d-block mb-2">
                          Name
                        </span>
                        <TextField
                          name="name"
                          variant="standard"
                          placeholder="Name"
                          className="w-100"
                          value={get(values, "name")}
                          defaultValue={get(values, "name")}
                          error={errors.name && touched.name}
                          helperText={
                            errors.name && touched.name ? errors.name : ""
                          }
                          maxRows={4}
                          onChange={(e) =>
                            setFieldValue("name", e.target.value)
                          }
                          onBlur={handleBlur}
                        />
                      </div>

                      <div className="mt-4">
                        <span className="font-weight-bold text-secondary d-block mb-2">
                          Description
                        </span>
                        <TextField
                          name="description"
                          variant="standard"
                          placeholder="Description"
                          className="w-100"
                          value={get(values, "description")}
                          defaultValue={get(values, "description")}
                          error={errors.description && touched.description}
                          helperText={
                            errors.description && touched.description
                              ? errors.description
                              : ""
                          }
                          multiline
                          maxRows={4}
                          onChange={(e) =>
                            setFieldValue("description", e.target.value)
                          }
                          onBlur={handleBlur}
                        />
                      </div>

                      <div className="mt-4">
                        <div className="d-flex mb-3">
                          <div className="font-weight-bold text-secondary d-block mb-2 mt-2">
                            CRT File
                          </div>
                          <div className="ml-3">
                            <Dropzone
                              onDrop={(acceptedFiles) => {
                                const file = acceptedFiles[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (e) => {
                                    const content = e.target.result;
                                    setFieldValue("certificate", content);
                                  };
                                  reader.readAsText(file);
                                }
                              }}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <section>
                                  <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <button
                                      type="button"
                                      className="SelectButton MainButton__primary"
                                    >
                                      Upload
                                    </button>
                                  </div>
                                </section>
                              )}
                            </Dropzone>
                          </div>
                        </div>

                        <TextField
                          multiline
                          rows={5}
                          name="certificate"
                          defaultValue={values.certificate}
                          value={values.certificate}
                          onChange={(e) =>
                            setFieldValue("certificate", e.target.value)
                          }
                          onBlur={handleBlur}
                          className="w-100 no-legend"
                          error={errors.certificate && touched.certificate}
                          helperText={
                            errors.certificate && touched.certificate
                              ? errors.certificate
                              : ""
                          }
                        />
                      </div>

                      <div className="mt-4">
                        <div className="d-flex mb-3">
                          <div className="font-weight-bold text-secondary d-block mb-2 mt-2">
                            KEY File
                          </div>
                          <div className="ml-3">
                            <Dropzone
                              onDrop={(acceptedFiles) => {
                                const file = acceptedFiles[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (e) => {
                                    const content = e.target.result;
                                    setFieldValue("key", content);
                                  };
                                  reader.readAsText(file);
                                }
                              }}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <section>
                                  <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <button
                                      type="button"
                                      className="SelectButton MainButton__primary"
                                    >
                                      Upload
                                    </button>
                                  </div>
                                </section>
                              )}
                            </Dropzone>
                          </div>
                        </div>

                        <TextField
                          multiline
                          rows={5}
                          name="key"
                          defaultValue={values.key}
                          value={values.key}
                          onChange={(e) => setFieldValue("key", e.target.value)}
                          onBlur={handleBlur}
                          className="w-100 no-legend"
                          error={errors.key && touched.key}
                          helperText={
                            errors.key && touched.key ? errors.key : ""
                          }
                        />
                      </div>

                      <div className="mt-4">
                        <div className="d-flex mb-3">
                          <div className="font-weight-bold text-secondary d-block mb-2 mt-2">
                            PFX File
                          </div>
                          <div className="ml-3">
                            <Dropzone
                              onDrop={(acceptedFiles) => {
                                const file = acceptedFiles[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (e) => {
                                    const content = e.target.result;
                                    setFieldValue("pfx", content);
                                  };
                                  reader.readAsText(file);
                                }
                              }}
                            >
                              {({ getRootProps, getInputProps }) => (
                                <section>
                                  <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <button
                                      type="button"
                                      className="SelectButton MainButton__primary"
                                    >
                                      Upload
                                    </button>
                                  </div>
                                </section>
                              )}
                            </Dropzone>
                          </div>
                        </div>

                        <TextField
                          multiline
                          rows={5}
                          name="pfx"
                          defaultValue={values.pfx}
                          value={values.pfx}
                          onChange={(e) => setFieldValue("pfx", e.target.value)}
                          onBlur={handleBlur}
                          className="w-100 no-legend"
                          error={errors.pfx && touched.pfx}
                          helperText={
                            errors.pfx && touched.pfx ? errors.pfx : ""
                          }
                        />
                      </div>

                      <div className="mt-4">
                        <span className="font-weight-bold text-secondary d-block mb-2">
                          Passphrase
                        </span>

                        <div className="w-100">
                          <InputBase
                            name="passphrase"
                            placeholder="Passphrase"
                            autoComplete="new-password"
                            value={values.passphrase}
                            onBlur={handleBlur}
                            style={{
                              borderBottom: "0.5px solid #000",
                              padding: "2px",
                              width: "100%",
                            }}
                            type={
                              values.showPassword
                                ? "password"
                                : values.showPassword === undefined
                                ? "password"
                                : "text"
                            }
                            onChange={handleChange}
                            className="customtextfield p-2 w-100"
                            error={errors.passphrase && touched.passphrase}
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() =>
                                    setFieldValue(
                                      "showPassword",
                                      !values.showPassword
                                    )
                                  }
                                  edge="end"
                                >
                                  {values.showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                          {errors.password && touched.password && (
                            <small className="text-danger ml-2 d-block mb-2">
                              {errors.password}
                            </small>
                          )}
                        </div>
                      </div>
                      {console.log(values, "values")}

                      <div className="mt-5">
                        <button
                          type="button"
                          onClick={handleUnmount}
                          className="MainButton MainButton__primary mr-3"
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className={`MainButton MainButton__primary outlined mr-3 ${
                            isSubmitting && "loading"
                          }`}
                          disabled={isSubmitting}
                        >
                          {pageMode === PAGE_MODES.CREATE ? "Create" : "Update"}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </Formik>
            </div>
          )}
        </PoonaWallaModal>
      )}
    </>
  );
};

export default CertificateModal;
